<template>
  <div class="page max-w-[1010px]">
    <div class="header-container">
      <div
        class="header"
        data-test="header"
      >
        Fundraisers
      </div>
      <Button
        v-if="fundraiserStore.fundraiserCreationAllowed"
        class="p-button-outlined p-button-lg"
        :style="{ color: '#000' }"
        @click="() => (fundraiserStore.createFundraiser = true)"
        data-test="create-new-button"
      >
        <i class="far fa-plus-circle"></i>
        <span>Create New</span>
      </Button>
    </div>
    <div
      v-if="donorStore.fundraisers && donorStore.fundraisers.length > 0"
      class="flex options-bar justify-between items-center"
      data-test="filter-bar"
    >
      <Filter
        :showFrequencyFilter="false"
        :searchPlaceholder="'Search Fundraisers, Organizations'"
        :dateFilters="dateFilters"
        :selectedDate="selectedDate"
        :statusFilters="statusOptions"
        :selectedStatus="selectedStatus"
        :sortOptions="sortOptions"
        :selectedSort="selectedSort"
        @update:modelValue="searchBy"
        @applyFilters="applyFilters"
        @resetFilters="resetFilters"
      />
    </div>
    <div
      v-if="donorStore.fundraisers && donorStore.fundraisers.length == 0 && !blankFundraisersCheck"
      class="flex options-bar justify-center"
      data-test="skeleton-loader"
    >
      <Skeletor
        height="32px"
        class="skeletonTag rounded-lg mb-4 mt-4 md:mt-0"
        width="286px"
      />
    </div>
    <!-- Fundraiser Blank State -->
    <div
      v-if="blankFundraisersCheck"
      class="centered-content"
      data-test="blank-state"
    >
      <div class="blank-title">Maximize your impact by creating a fundraiser!</div>
      <div style="padding-top: 16px; padding-bottom: 16px">
        Creating a fundraiser is a great way to engage your peers and help your favorite organizations.
      </div>
      <div>
        <titan-button
          type="primary"
          size="large"
          fill="outline"
          :style="`color: #000;`"
          @click="redirectToCMSFAQ"
          data-test="learn-more-button"
        >
          Learn More</titan-button
        >
      </div>
      <FundraiserCreation v-if="fundraiserStore.createFundraiser"></FundraiserCreation>
    </div>

    <!-- Fundraiser Cards -->
    <div
      class="fundraiser-cards-container"
      data-test="fundraiser-cards-container"
    >
      <div
        v-if="donorStore.fundraisers && donorStore.fundraisers.length == 0 && !blankFundraisersCheck"
        class="flex flex-col md:flex-row"
      >
        <Skeletor
          v-for="(skeleton, index) in 3"
          :key="index"
          height="368px"
          class="skeletonTag rounded-lg mb-8 md:mb-0 md:mr-8"
          width="288px"
        />
      </div>
      <FundraiserCard
        v-else
        v-for="fundraiser in filteredFundraisers"
        :key="fundraiser.fundraisers_id"
        :fundraiser="fundraiser"
        data-test="fundraiser-card"
      />
    </div>
    <FundraiserCreation v-if="fundraiserStore.createFundraiser"></FundraiserCreation>
  </div>
</template>
<script setup>
import { ref, watch, onBeforeMount, computed } from 'vue';
import filters from '@/helpers/filters';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore';
import { useDonorStore } from '@/stores/DonorStore.js';
import FundraiserCard from '@/components/fundraisers/FundraiserCard.vue';
import FundraiserCreation from './FundraiserCreation.vue';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import Filter from '@/components/data/Filter.vue';

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();

// Store the initial order of fundraisers
const initialFundraisersOrder = ref([...donorStore.fundraisers]);

const blankFundraisersCheck = computed(() => {
  return !donorStore.fundraisersLoading && donorStore.fundraisers.length === 0;
});

const defaultFilterSelections = {
  status: 'all',
  date: 'all',
  sortBy: 'sortByStatus',
  searchTerm: '',
};
const fundraiserFilterSelections = ref({ ...defaultFilterSelections });

const searchParams = new URLSearchParams(window.location.search);
if (searchParams.has('status')) {
  fundraiserFilterSelections.value.status = searchParams.get('status');
}

const dateFilters = ref([
  { label: 'All', value: 'all' },
  { label: 'Past 7 Days', value: 'past7Days' },
  { label: 'Past Month', value: 'pastMonth' },
  { label: 'Past Year', value: 'pastYear' },
]);

const statusOptions = ref([
  { label: 'All', value: 'all' },
  { label: 'Created', value: 'created' },
  { label: 'Deactivated', value: 'deactivated' },
  { label: 'Expired', value: 'expired' },
  { label: 'Published', value: 'published' },
]);

const selectedStatus = ref(null);

const selectedSort = ref(null);
const sortOptions = computed(() => [
  { label: 'Default', value: 'default' },
  { label: 'Amount Raised: Low to High', value: 'amountRaisedLowHigh' },
  { label: 'Amount Raised: High to Low', value: 'amountRaisedHighLow' },
  { label: 'Goal: Low to High', value: 'goalLowHigh' },
  { label: 'Goal: High to Low', value: 'goalHighLow' },
  { label: 'Fundraiser Name: A to Z', value: 'fundraiserNameAToZ' },
  { label: 'Fundraiser Name: Z to A', value: 'fundraiserNameZToA' },
]);

const filteredFundraisers = ref([...initialFundraisersOrder.value]);
const resetFiltersCalled = ref(0);

const getFilteredFundraisers = () => {
  filteredFundraisers.value = [...initialFundraisersOrder.value];

  filterByStatus();

  filterByDate();

  filterBySearchTerm();

  sortFundraisers();

  return filteredFundraisers.value;
};

const filterByStatus = () => {
  if (fundraiserFilterSelections.value.status !== 'all') {
    const status = fundraiserFilterSelections.value.status;
    switch (status) {
      case 'published':
        filteredFundraisers.value = donorStore.publishedFundraisers;
        break;
      case 'created':
        filteredFundraisers.value = donorStore.createdFundraisers;
        break;
      case 'deactivated':
        filteredFundraisers.value = donorStore.deactivatedFundraisers;
        break;
      case 'expired':
        filteredFundraisers.value = donorStore.expiredFundraisers;
        break;
      default:
        break;
    }
  }
};

const filterByDate = () => {
  if (fundraiserFilterSelections.value.date !== 'all') {
    filteredFundraisers.value = filters.filterByDate(filteredFundraisers.value, fundraiserFilterSelections.value.date);
  }
};

const filterBySearchTerm = () => {
  const searchTerm = fundraiserFilterSelections.value.searchTerm.toLowerCase();
  if (searchTerm !== '') {
    filteredFundraisers.value = filteredFundraisers.value.filter(
      (fundraiser) =>
        fundraiser.title.toLowerCase().includes(searchTerm) ||
        fundraiser.associatedOrgName.toLowerCase().includes(searchTerm),
    );
  }
};

const sortItems = (items, sortBy) => {
  switch (sortBy) {
    case 'amountRaisedLowHigh':
    case 'amountRaisedHighLow':
      return filters.sortByAmount(items, sortBy, 'amountRaisedHighLow', 'amountRaisedLowHigh', 'amountRaised_cached');
    case 'goalLowHigh':
    case 'goalHighLow':
      return filters.sortByAmount(items, sortBy, 'goalHighLow', 'goalLowHigh', 'goal');
    case 'fundraiserNameAToZ':
    case 'fundraiserNameZToA':
      return filters.sortByName(items, sortBy, 'fundraiserNameAToZ', 'fundraiserNameZToA', 'title');
    case 'default':
      return [...initialFundraisersOrder.value];
    default:
      return items;
  }
};

const sortFundraisers = () => {
  filteredFundraisers.value = sortItems(filteredFundraisers.value, fundraiserFilterSelections.value.sortBy);
};

const searchBy = (val) => {
  if (val != undefined) {
    fundraiserFilterSelections.value.searchTerm = val;
    getFilteredFundraisers();
  }
};

const resetFilters = () => {
  fundraiserFilterSelections.value = { ...defaultFilterSelections };
  selectedDate.value = null;
  selectedStatus.value = null;
  selectedSort.value = null;
  searchBy('');
  resetFiltersCalled.value++; // Increment to trigger the watcher
};

const applyFilters = (filters) => {
  if (filters.selectedDate) {
    fundraiserFilterSelections.value.date = filters.selectedDate.value;
  }
  if (filters.selectedStatus) {
    fundraiserFilterSelections.value.status = filters.selectedStatus.value;
  }
  if (filters.selectedSort) {
    fundraiserFilterSelections.value.sortBy = filters.selectedSort.value;
  }
  getFilteredFundraisers();
};

watch(
  () => donorStore.fundraisers,
  () => {
    initialFundraisersOrder.value = [...donorStore.fundraisers];
    getFilteredFundraisers();
  },
);

watch(resetFiltersCalled, () => {
  getFilteredFundraisers();
});

watch(selectedStatus, (newStatus) => {
  filterByStatus();
  getFilteredFundraisers();
});

// Computed property to check if any filters are active
const filtersActive = ref(false);
watch(
  () => fundraiserFilterSelections.value,
  () => {
    // Check if any filter selection is not default ('all')
    filtersActive.value =
      fundraiserFilterSelections.value.searchTerm !== '' ||
      fundraiserFilterSelections.value.status !== 'all' ||
      fundraiserFilterSelections.value.date !== 'all' ||
      fundraiserFilterSelections.value.sortBy !== 'sortByStatus';
  },
  { deep: true },
);

// Function to parse URL parameters
const getUrlParameter = (name) => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

// Check if URL contains 'org_id=X' and set benefittingOrg if org_id is found
// This happens if it is a redirect from the front end site
const checkUrlOrgOrCompany = async () => {
  const orgHostsId = getUrlParameter('org');
  const companyGivingId = getUrlParameter('company');

  fundraiserStore.benefittingOrgHostsId = orgHostsId;
  fundraiserStore.benefittingCompanyGivingId = companyGivingId;

  if (fundraiserStore.benefittingOrg || fundraiserStore.benefittingCompany) {
    fundraiserStore.createFundraiser = true;
  }
  if (fundraiserStore.benefittingOrg) {
    fundraiserStore.disabledOrg = true;
  }
  if (fundraiserStore.benefittingCompany) {
    fundraiserStore.companyGiving = fundraiserStore.benefittingCompany;
  }
};

const redirectToCMSFAQ = () => {
  window.location.href = donorStore.domain.replace(/\/$/, '') + '/faq';
};

const selectedDate = ref(null);

onBeforeMount(async () => {
  const requests = [fundraiserStore.bringAllOrganizations(), fundraiserStore.checkFundraiserCreation()];
  if (donorStore.featureCompanyGiving) {
    requests.push(companyAdminStore.getCompanyCollection());
  }
  await Promise.all(requests);
  await checkUrlOrgOrCompany();
});
</script>

<style scoped>
.actions-icon {
  color: #000;
  font-family: 'Font Awesome 5 Pro';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
}
.header-container {
  display: flex;
  align-items: center; /* This will vertically center the items in the container */
  justify-content: space-between; /* This will place the header on the left and the button on the right */
}
.options-bar {
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.filter {
  margin: 0 10px;
  z-index: 1;
}
.search {
  width: 288px;
  flex-shrink: 0;
}
.search-filters {
  align-items: center;
}
.fundraiser-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  margin-bottom: 100px;
}
.fundraiser-image {
  width: 288px;
  height: 144px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  overflow: hidden;
}
.fundraiser-name {
  width: 219px;
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin-top: 9px;
}
.org-name {
  width: 237px;
  color: #000;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-bottom: 12px;
}
.status-badge {
  width: 135px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 12px;
}
.action-status {
  display: flex;
  justify-content: space-between;
  align-items: center; /* Optional: aligns items vertically in the center */
  width: 100%; /* Ensures the div spans the full width of its parent */
}
.progress-bar {
  width: 262px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 4px;
  background: var(--Purple-50, #e6e9ee);
}
.amount-raised {
  width: 240px;
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  margin-top: 8px;
  margin-bottom: 16px;
}

@media (max-width: 1024px) {
  .header {
    text-align: center; /* Centers the text horizontally */
    width: 100%; /* Ensures the div takes up the full width */
    margin: 0 auto; /* Centers the div within its parent container */
    padding: 20px 0; /* Adds some vertical padding for spacing */
  }
  .header-container,
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center; /* Centers items vertically */
    justify-content: center; /* Helps with horizontal centering if needed */
    width: 100%; /* Ensures full width */
    margin-right: 0; /* Resets any right margin */
  }

  .search-filters {
    order: 2; /* Ensures search filters come right after header */
    margin-top: 20px; /* Adds some space above for visual separation */
  }

  .filters-desktop {
    order: 3; /* Ensures filters are displayed below the search bar */
    flex-wrap: wrap; /* Allows filters to wrap in smaller screens */
    margin-top: 10px; /* Adds space between search bar and filters */
  }

  .fundraiser-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
}

@media (max-width: 768px) {
  .header {
    text-align: center; /* Centers the text horizontally */
    width: 100%; /* Ensures the div takes up the full width */
    margin: 0 auto; /* Centers the div within its parent container */
    padding: 20px 0; /* Adds some vertical padding for spacing */
  }
  .header-container,
  .search-filters,
  .filters-desktop {
    display: flex;
    flex-direction: column;
    align-items: center; /* Centers items vertically */
    justify-content: center; /* Helps with horizontal centering if needed */
    width: 100%; /* Ensures full width */
    margin-right: 0; /* Resets any right margin */
  }

  .search-filters {
    order: 2; /* Ensures search filters come right after header */
    margin-top: 20px; /* Adds some space above for visual separation */
  }

  .filters-desktop {
    order: 3; /* Ensures filters are displayed below the search bar */
    flex-wrap: wrap; /* Allows filters to wrap in smaller screens */
    margin-top: 10px; /* Adds space between search bar and filters */
  }

  .fundraiser-cards-container {
    justify-content: center; /* Centers cards horizontally */
    margin-right: 0; /* Removes any right margin that might push content to the left */
    padding: 0 20px; /* Adds padding on the sides */
  }
}
</style>
